<template>
  <APageHead
    :title="$t('label.78')"
    :sub="$t('txt.14')"
    icon="receipt_long"
  >
    <template #actions>
      <ABtn kind="ghost" icon="refresh" @click="getData">
        {{ $t('label.79') }}
      </ABtn>
    </template>
  </APageHead>

  <div class="sc-page">
    <!-- Statistics 4종 -->
    <div class="sc-stats">
      <AStat :label="$t('label.80')" :value="stats.totalCount" icon="description" />
      <AStat
        :label="$t('label.81')"
        :value="stats.pendingCount"
        icon="pending"
        :accent="stats.pendingCount > 0"
      />
      <AStat :label="$t('label.82')" :value="stats.completedCount" icon="check_circle" />
      <AStat :label="$t('label.83')" :value="formatCurrency(stats.totalAmount, 'KRW')" icon="payments" />
    </div>

    <!-- 탭 필터 -->
    <TabNav
      v-model="activeTab"
      :tabs="tabNavItems"
    />

    <!-- 검색 필터 -->
    <ACard :padding="16">
      <div class="sc-filter">
        <div class="sc-filter__field">
          <div class="p-input-group">
            <i class="material-icons-round sc-filter__icon">search</i>
            <input
              type="text"
              class="p-input"
              v-model="searchKeyword"
              :placeholder="$t('txt.18')"
              @keyup.enter="getData"
            />
          </div>
        </div>
        <div class="sc-filter__field sc-filter__field--narrow">
          <div class="p-input-group">
            <i class="material-icons-round sc-filter__icon">date_range</i>
            <select class="p-input" v-model="selectedYear" @change="getData">
              <option value="">{{ $t('label.86') }}</option>
              <option v-for="year in yearOptions" :key="year" :value="year">
                {{ year }} {{ $t('label.87') }}
              </option>
            </select>
          </div>
        </div>
        <div class="sc-filter__field sc-filter__field--narrow">
          <div class="p-input-group">
            <i class="material-icons-round sc-filter__icon">calendar_month</i>
            <select class="p-input" v-model="selectedMonth" @change="getData">
              <option value="">{{ $t('label.88') }}</option>
              <option v-for="month in 12" :key="month" :value="month">
                {{ month }} {{ $t('label.89') }}
              </option>
            </select>
          </div>
        </div>
        <ABtn kind="primary" icon="search" @click="getData">
          {{ $t('label.90') }}
        </ABtn>
      </div>
    </ACard>

    <!-- Invoice Table -->
    <ACard :padding="0">
      <template #head>
        <h3 class="sc-card-title">{{ $t('label.91') }}</h3>
        <p v-if="totalCount > 0" class="sc-card-sub">
          {{ $t('label.92', { count: totalCount }) }}
        </p>
      </template>

      <ATable
        :columns="invoiceCols"
        :rows="tableData"
        row-key="idx"
        :empty-text="$t('txt.20')"
      >
        <template #cell:num="{ index }">
          <span class="sc-num">{{ getItemNumber(index) }}</span>
        </template>

        <template #cell:invoice="{ row }">
          <span class="sc-invoice-no">{{ row.invoice_number }}</span>
        </template>

        <template #cell:editor="{ row }">
          <div class="sc-editor">
            <div class="sc-editor__avatar">{{ row.editor_name?.charAt(0) || 'E' }}</div>
            <div class="sc-editor__text">
              <div class="sc-editor__name">{{ row.editor_name }}</div>
              <div class="sc-editor__email">{{ row.editor_email }}</div>
            </div>
          </div>
        </template>

        <template #cell:period="{ row }">
          <span>{{ row.settlement_year }}년 {{ row.settlement_month }}월</span>
        </template>

        <template #cell:amount="{ row }">
          <span class="sc-amount">{{ formatCurrency(row.total_amount, row.currency_code) }}</span>
        </template>

        <template #cell:email="{ row }">
          <APill
            :kind="row.email_sent ? 'ok' : 'default'"
            :icon="row.email_sent ? 'check' : 'schedule'"
          >
            {{ row.email_sent ? $t('label.98') : $t('label.99') }}
          </APill>
        </template>

        <template #cell:status="{ row }">
          <APill
            :kind="paymentStatusPillKind(row.payment_status)"
            :icon="getPaymentStatusIcon(row.payment_status)"
          >
            {{ getPaymentStatusLabel(row.payment_status) }}
          </APill>
        </template>

        <template #cell:created="{ row }">
          <span class="sc-date">{{ formatDateTime(row.created_at) }}</span>
        </template>

        <template #cell:actions="{ row }">
          <div class="sc-actions">
            <button
              type="button"
              class="p-icon-btn"
              :title="$t('label.100')"
              @click="openDetailModal(row)"
            >
              <i class="material-icons-round">visibility</i>
            </button>
            <button
              type="button"
              class="p-icon-btn"
              :title="$t('label.101')"
              @click="downloadPdf(row)"
            >
              <i class="material-icons-round">download</i>
            </button>
            <button
              type="button"
              class="p-icon-btn"
              :title="$t('label.103')"
              @click="openSettlementDetailModal(row)"
            >
              <i class="material-icons-round">receipt_long</i>
            </button>
          </div>
        </template>
      </ATable>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="sc-foot">
        <Pagination
          v-model:current-page="currentPage"
          :total-pages="totalPages"
          @update:current-page="getData"
        />
      </div>
    </ACard>

    <!-- Detail Modal -->
    <AModal
      v-model="showDetailModal"
      :title="$t('label.107')"
      icon="receipt"
      size="lg"
    >
      <template v-if="selectedInvoice">
        <div class="sc-detail-grid">
          <div class="sc-detail-section">
            <p class="p-section-label">
              <i class="material-icons-round">info</i>
              {{ $t('label.108') }}
            </p>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.84') }}</span>
              <span class="sc-detail-value sc-invoice-no">{{ selectedInvoice.invoice_number }}</span>
            </div>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.94') }}</span>
              <span class="sc-detail-value">{{ selectedInvoice.settlement_year }}년 {{ selectedInvoice.settlement_month }}월</span>
            </div>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.97') }}</span>
              <span class="sc-detail-value">{{ formatDateTime(selectedInvoice.created_at) }}</span>
            </div>
          </div>

          <div class="sc-detail-section">
            <p class="p-section-label">
              <i class="material-icons-round">person</i>
              {{ $t('label.117') }}
            </p>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.69') }}</span>
              <span class="sc-detail-value">{{ selectedInvoice.editor_name }}</span>
            </div>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.01') }}</span>
              <span class="sc-detail-value">{{ selectedInvoice.editor_email }}</span>
            </div>
          </div>

          <div class="sc-detail-section">
            <p class="p-section-label">
              <i class="material-icons-round">payments</i>
              {{ $t('label.120') }}
            </p>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.121') }}</span>
              <span class="sc-detail-value sc-amount sc-amount--big">
                {{ formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency_code) }}
              </span>
            </div>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.122') }}</span>
              <APill :kind="paymentStatusPillKind(selectedInvoice.payment_status)">
                {{ getPaymentStatusLabel(selectedInvoice.payment_status) }}
              </APill>
            </div>
            <div class="sc-detail-row" v-if="selectedInvoice.paid_at">
              <span class="sc-detail-label">{{ $t('label.123') }}</span>
              <span class="sc-detail-value">{{ formatDateTime(selectedInvoice.paid_at) }}</span>
            </div>
            <div class="sc-detail-row" v-if="selectedInvoice.payment_memo">
              <span class="sc-detail-label">{{ $t('label.104') }}</span>
              <span class="sc-detail-value">{{ selectedInvoice.payment_memo }}</span>
            </div>
          </div>

          <div class="sc-detail-section">
            <p class="p-section-label">
              <i class="material-icons-round">mail</i>
              {{ $t('label.112') }}
            </p>
            <div class="sc-detail-row">
              <span class="sc-detail-label">{{ $t('label.113') }}</span>
              <APill :kind="selectedInvoice.email_sent ? 'ok' : 'default'">
                {{ selectedInvoice.email_sent ? $t('label.114') : $t('label.115') }}
              </APill>
            </div>
            <div class="sc-detail-row" v-if="selectedInvoice.email_sent_at">
              <span class="sc-detail-label">{{ $t('label.116') }}</span>
              <span class="sc-detail-value">{{ formatDateTime(selectedInvoice.email_sent_at) }}</span>
            </div>
          </div>
        </div>
      </template>

      <template #foot>
        <ABtn kind="ghost" @click="showDetailModal = false">{{ $t('label.109') }}</ABtn>
        <ABtn kind="primary" icon="download" @click="downloadPdf(selectedInvoice)">
          {{ $t('label.101') }}
        </ABtn>
      </template>
    </AModal>

    <!-- Settlement Detail Modal -->
    <AModal
      v-model="showSettlementDetailModal"
      :title="$t('label.103')"
      icon="receipt_long"
      size="xl"
    >
      <div v-if="isLoadingSettlement" class="sc-loading">
        <div class="p-spinner" />
        <p class="sc-loading-text">{{ $t('label.106') }}</p>
      </div>

      <template v-else-if="settlementDetail">
        <!-- 요약 통계 3종 -->
        <div class="sc-summary">
          <div class="sc-summary__card sc-summary__card--ok">
            <i class="material-icons-round sc-summary__icon">payments</i>
            <div class="sc-summary__text">
              <span class="sc-summary__label">{{ $t('label.124') }}</span>
              <span class="sc-summary__value">{{ formatCurrency(settlementDetail.total_royalty_amount, 'KRW') }}</span>
            </div>
          </div>
          <div class="sc-summary__card sc-summary__card--warn">
            <i class="material-icons-round sc-summary__icon">shopping_cart</i>
            <div class="sc-summary__text">
              <span class="sc-summary__label">{{ $t('label.111') }}</span>
              <span class="sc-summary__value">{{ settlementDetail.total_payment_count }}건</span>
            </div>
          </div>
          <div class="sc-summary__card sc-summary__card--info">
            <i class="material-icons-round sc-summary__icon">library_books</i>
            <div class="sc-summary__text">
              <span class="sc-summary__label">{{ $t('label.118') }}</span>
              <span class="sc-summary__value">{{ settlementDetail.settlement_count }}건</span>
            </div>
          </div>
        </div>

        <!-- 영업비 안내 (가이드 §10.3) -->
        <p
          v-if="settlementDetail.settlements && settlementDetail.settlements.length > 0"
          class="sc-fee-note"
        >
          <i class="material-icons-round">info</i>
          {{ $t('txt.25') }}
        </p>

        <!-- 정산 항목 -->
        <AEmpty
          v-if="!settlementDetail.settlements || settlementDetail.settlements.length === 0"
          :title="$t('txt.20')"
        />

        <div
          v-for="(settlement, sIndex) in settlementDetail.settlements"
          :key="settlement.idx"
          class="sc-settlement"
        >
          <header class="sc-settlement__head">
            <div class="sc-settlement__title-wrap">
              <span class="sc-settlement__num">{{ sIndex + 1 }}</span>
              <div>
                <h4 class="sc-settlement__title">{{ settlement.textbook_title }}</h4>
                <p class="sc-settlement__meta">
                  {{ settlement.editor_name }} · {{ settlement.textbook_barcode }}
                </p>
              </div>
            </div>
            <div class="sc-settlement__right">
              <APill :kind="settlementStatusPillKind(settlement.status)">
                {{ getSettlementStatusLabel(settlement.status) }}
              </APill>
              <span class="sc-amount sc-amount--big">
                {{ formatCurrency(settlement.royalty_amount, settlement.royalty_currency) }}
              </span>
            </div>
          </header>

          <div class="sc-settlement__info">
            <div class="sc-mini-info">
              <span class="sc-mini-info__label">{{ $t('label.94') }}</span>
              <span class="sc-mini-info__value">{{ settlement.settlement_year }}년 {{ settlement.settlement_month }}월</span>
            </div>
            <div class="sc-mini-info">
              <span class="sc-mini-info__label">{{ $t('label.119') }}</span>
              <span class="sc-mini-info__value">{{ settlement.payment_count }}건</span>
            </div>
            <div class="sc-mini-info">
              <span class="sc-mini-info__label">{{ $t('label.125') }}</span>
              <span class="sc-mini-info__value">{{ settlement.exchange_rate }}</span>
            </div>
            <div class="sc-mini-info">
              <span class="sc-mini-info__label">{{ $t('label.32') }}</span>
              <span class="sc-mini-info__value">{{ settlement.amount_currency }}</span>
            </div>
          </div>

          <!-- 결제 상세 -->
          <ATable
            :columns="paymentCols"
            :rows="settlement.payments || []"
            row-key="idx"
            :empty-text="$t('txt.22')"
          >
            <template #cell:num="{ index }">
              <span class="sc-num">{{ index + 1 }}</span>
            </template>
            <template #cell:learner="{ row }">
              <div class="sc-editor">
                <div class="sc-editor__avatar sc-editor__avatar--sm">{{ row.learner_name?.charAt(0) || 'L' }}</div>
                <span class="sc-editor__name">{{ row.learner_name }}</span>
              </div>
            </template>
            <template #cell:amount="{ row }">
              <span class="sc-amount">{{ formatCurrency(row.amount, row.currency_code) }}</span>
            </template>
            <template #cell:coupon="{ row }">
              <span :class="row.coupon_amount ? 'sc-coupon' : 'sc-muted'">
                {{ row.coupon_amount ? '-' + formatCurrency(row.coupon_amount, row.currency_code) : '-' }}
              </span>
            </template>
            <template #cell:method="{ row }">
              <APill :kind="paymentMethodPillKind(row.payment_method)">
                {{ getPaymentMethodLabel(row.payment_method) }}
              </APill>
            </template>
            <template #cell:pgFee="{ row }">
              <span class="sc-muted sc-mono">{{ formatRate(row.pg_fee_rate) }}</span>
            </template>
            <!-- 영업비율: PG 수수료와 기준 금액이 달라 합산하지 않고 분리 표기 (가이드 §10.3) -->
            <template #cell:salesFee="{ row }">
              <span :class="hasSalesFee(row) ? 'sc-mono sc-sales-fee' : 'sc-muted sc-mono'">
                {{ hasSalesFee(row) ? formatRate(row.sales_fee_rate) : '-' }}
              </span>
            </template>
            <!-- 정액(amount) 방식은 인세율(0%) 대신 1copy당 금액 표시 (가이드 §11) -->
            <template #cell:royaltyRate="{ row }">
              <APill v-if="isPerCopyRoyalty(row)" kind="yellow">{{ perCopyLabel(row) }}</APill>
              <APill v-else kind="info">{{ row.royalty_rate }}%</APill>
            </template>
            <template #cell:royalty="{ row }">
              <span class="sc-amount">{{ formatCurrency(row.royalty_amount, row.currency_code) }}</span>
            </template>
            <template #cell:date="{ row }">
              <span class="sc-date">{{ formatDateTime(row.created_at) }}</span>
            </template>
          </ATable>

          <footer class="sc-settlement__foot">
            <span class="sc-settlement__foot-label">소계</span>
            <span class="sc-amount sc-amount--big">
              {{ formatCurrency(settlement.royalty_amount, settlement.royalty_currency) }}
            </span>
          </footer>
        </div>

        <!-- Grand Total -->
        <div
          v-if="settlementDetail.settlements && settlementDetail.settlements.length > 0"
          class="sc-grand-total"
        >
          <span class="sc-grand-total__label">{{ $t('label.124') }}</span>
          <span class="sc-amount sc-amount--xlarge">
            {{ formatCurrency(settlementDetail.total_royalty_amount, 'KRW') }}
          </span>
        </div>
      </template>

      <template #foot>
        <ABtn kind="ghost" @click="showSettlementDetailModal = false">
          {{ $t('label.109') }}
        </ABtn>
        <ABtn
          kind="primary"
          icon="download"
          :disabled="!settlementDetail || !settlementDetail.settlements || settlementDetail.settlements.length === 0"
          @click="exportSettlementToExcel"
        >
          {{ $t('label.134') }}
        </ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination.vue';
import TabNav from '@/components/TabNav.vue';
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AModal from '@/components/Pineapple/AModal.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import * as XLSX from 'xlsx';

export default {
  name: "SettlementCompleted",
  components: {
    Pagination, TabNav,
    APageHead, ACard, AStat, ATable, APill, ABtn, AModal, AEmpty,
  },

  data() {
    return {
      searchKeyword: '',
      selectedYear: '',
      selectedMonth: '',
      activeTab: 'all',
      yearOptions: this.generateYearOptions(),

      tableData: [],
      totalCount: 0,
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 20,

      stats: {
        totalCount: 0,
        pendingCount: 0,
        completedCount: 0,
        totalAmount: 0,
      },

      tabs: [
        { value: 'all', label: this.$t('label.135'), icon: 'list', count: 0 },
        { value: 'pending', label: this.$t('label.81'), icon: 'pending', count: 0 },
        { value: 'completed', label: this.$t('label.82'), icon: 'check_circle', count: 0 },
      ],

      showDetailModal: false,
      showSettlementDetailModal: false,
      showDropdown: null,
      selectedInvoice: null,

      settlementDetail: null,
      isLoadingSettlement: false,

      invoiceCols: [
        { key: 'num',      label: this.$t('label.93'), align: 'center', width: 60 },
        { key: 'invoice',  label: this.$t('label.84'), align: 'left' },
        { key: 'editor',   label: this.$t('label.85'), align: 'left' },
        { key: 'period',   label: this.$t('label.94'), align: 'left' },
        { key: 'amount',   label: this.$t('label.95'), align: 'right' },
        { key: 'email',    label: this.$t('label.01'), align: 'center' },
        { key: 'status',   label: this.$t('label.96'), align: 'center' },
        { key: 'created',  label: this.$t('label.97'), align: 'left' },
        { key: 'actions',  label: '', align: 'right', width: 130 },
      ],

      paymentCols: [
        { key: 'num',          label: this.$t('label.93'),  align: 'center', width: 50 },
        { key: 'learner',      label: this.$t('label.126'), align: 'left' },
        { key: 'amount',       label: this.$t('label.127'), align: 'right' },
        { key: 'coupon',       label: this.$t('label.128'), align: 'right' },
        { key: 'method',       label: this.$t('label.129'), align: 'center' },
        { key: 'pgFee',        label: this.$t('label.130'), align: 'center' },
        { key: 'salesFee',     label: this.$t('label.141'), align: 'center' },
        { key: 'royaltyRate',  label: this.$t('label.131'), align: 'center' },
        { key: 'royalty',      label: this.$t('label.132'), align: 'right' },
        { key: 'date',         label: this.$t('label.133'), align: 'left' },
      ],
    };
  },

  computed: {
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
    tabNavItems() {
      return this.tabs.map(t => ({ id: t.value, label: t.label, icon: t.icon, count: t.count }));
    },
  },

  watch: {
    activeTab(v) { this.handleTabChange(v); },
  },

  mounted() {
    this.getData();
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear; i >= currentYear - 5; i--) years.push(i);
      return years;
    },

    async getData() {
      try {
        const params = {
          limit: this.itemsPerPage,
          page: this.currentPage,
        };
        if (this.selectedYear) params.year = this.selectedYear;
        if (this.selectedMonth) params.month = this.selectedMonth;
        if (this.searchKeyword) params.keyword = this.searchKeyword;
        if (this.activeTab !== 'all') params.payment_status = this.activeTab;

        const res = await this.$axios({
          method: "get",
          url: "/buy-invoice/editor/list",
          params,
          loadingBar: true,
        });

        if (res.status === 200) {
          this.tableData = res.data.items || [];
          this.totalCount = res.data.total_count || 0;
          this.totalPages = Math.ceil(this.totalCount / this.itemsPerPage) || 1;

          if (res.data.stats) {
            this.stats = {
              totalCount: res.data.stats.total_count || 0,
              pendingCount: res.data.stats.pending_count || 0,
              completedCount: res.data.stats.completed_count || 0,
              totalAmount: parseFloat(res.data.stats.total_amount) || 0,
            };
            this.tabs[0].count = res.data.stats.total_count || 0;
            this.tabs[1].count = res.data.stats.pending_count || 0;
            this.tabs[2].count = res.data.stats.completed_count || 0;
          }
        }
      } catch (error) {
        console.error("지급서 목록 조회 실패:", error);
        this.$notifyVue({
          type: "danger",
          title: '지급서 목록 조회 실패',
          text: error.response?.data?.detail || '다시 시도해 주세요.',
        });
        this.tableData = [];
        this.totalCount = 0;
        this.totalPages = 1;
      }
    },

    handleTabChange(tab) {
      this.activeTab = tab;
      this.currentPage = 1;
      this.getData();
    },

    toggleDropdown(id, event) {
      this.showDropdown = this.showDropdown === id ? null : id;
      event.stopPropagation();
    },

    handleOutsideClick(event) {
      if (!event.target.closest('.dropdown')) this.showDropdown = null;
    },

    openDetailModal(invoice) {
      this.showDropdown = null;
      this.selectedInvoice = { ...invoice };
      this.showDetailModal = true;
    },

    async openSettlementDetailModal(invoice) {
      this.showDropdown = null;
      this.showSettlementDetailModal = true;
      this.settlementDetail = null;
      this.isLoadingSettlement = true;

      try {
        const res = await this.$axios({
          method: "get",
          url: `/payment/buy-invoice/${invoice.idx}/settlements`,
          loadingBar: false,
        });
        if (res.status === 200) this.settlementDetail = res.data;
      } catch (error) {
        console.error("정산 상세 조회 실패:", error);
        this.$notifyVue({
          type: "danger",
          title: this.$t('alert.04'),
          text: error.response?.data?.detail || this.$t('txt.23'),
        });
        this.showSettlementDetailModal = false;
      } finally {
        this.isLoadingSettlement = false;
      }
    },

    async downloadPdf(invoice) {
      this.showDropdown = null;
      if (!invoice) return;
      try {
        await this.$axios_download(`/buy-invoice/download/${invoice.idx}?t=${Date.now()}`, {
          method: "get",
          fileName: `${invoice.invoice_number}.pdf`,
        });
        this.$notifyVue({ type: "success", title: this.$t('label.101'), text: this.$t('label.101') });
      } catch (error) {
        console.error(this.$t('alert.04'), error);
        this.$notifyVue({
          type: "danger",
          title: this.$t('alert.04'),
          text: error.response?.data?.detail || this.$t('txt.23'),
        });
      }
    },

    formatCurrency(amount, currencyCode = 'KRW') {
      const value = Number(amount) || 0;
      const code = (currencyCode || 'KRW').toUpperCase();
      try {
        return new Intl.NumberFormat('ko-KR', {
          style: 'currency',
          currency: code,
          minimumFractionDigits: code === 'KRW' ? 0 : 2,
        }).format(value);
      } catch (error) {
        // 알 수 없는 통화 코드 대비 (Intl 은 잘못된 코드에 RangeError 를 던진다)
        return `${value.toLocaleString('ko-KR')} ${code}`;
      }
    },

    // 비율(0.015 → 1.5%) 표기. 백엔드 미배포 시 sales_fee_rate 가 없으므로 호출부에서 0 폴백
    formatRate(rate) {
      const n = Number(rate);
      if (rate === null || rate === undefined || rate === '' || Number.isNaN(n)) return '-';
      return `${parseFloat((n * 100).toFixed(2))}%`;
    },

    // 영업비율이 실제로 붙은 결제인지 (미배포 시 필드 없음 → 0 폴백, 가이드 §10.2)
    hasSalesFee(payment) {
      return Number(payment?.sales_fee_rate) > 0;
    },

    // 정액(1copy당) 인세 여부 — 가이드 §11
    isPerCopyRoyalty(payment) {
      return payment?.royalty_type === 'amount';
    },
    perCopyLabel(payment) {
      const amount = payment?.per_copy_amount;
      if (amount === null || amount === undefined) return '-';
      const currency = payment.book_currency || payment.currency_code || 'KRW';
      return `${this.formatCurrency(amount, currency)} / copy`;
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    },

    paymentStatusPillKind(status) {
      const map = { pending: 'warn', completed: 'ok' };
      return map[status] || 'default';
    },
    getPaymentStatusIcon(status) {
      const icons = { pending: 'pending', completed: 'check_circle' };
      return icons[status] || 'help';
    },
    getPaymentStatusLabel(status) {
      const labels = { pending: this.$t('label.81'), completed: this.$t('label.82') };
      return labels[status] || status;
    },

    // Legacy class helpers (kept for any extra usage)
    getPaymentStatusClass(status) {
      const classes = { pending: 'badge bg-gradient-warning', completed: 'badge bg-gradient-success' };
      return classes[status] || 'badge bg-gradient-secondary';
    },
    getAvatarColor() { return null; },

    settlementStatusPillKind(status) {
      const map = { calculated: 'info', invoiced: 'warn', paid: 'ok' };
      return map[status] || 'default';
    },
    getSettlementStatusLabel(status) {
      const labels = {
        calculated: this.$t('label.136'),
        invoiced: this.$t('label.137'),
        paid: this.$t('label.82'),
      };
      return labels[status] || status;
    },
    getSettlementStatusClass(status) {
      const classes = {
        calculated: 'badge bg-gradient-info',
        invoiced: 'badge bg-gradient-warning',
        paid: 'badge bg-gradient-success',
      };
      return classes[status] || 'badge bg-gradient-secondary';
    },

    paymentMethodPillKind(method) {
      const map = { A: 'dark', G: 'ok', P: 'info', T: 'yellow', L: 'warn' };
      return map[method] || 'default';
    },
    getPaymentMethodClass(method) {
      const classes = {
        A: 'badge bg-dark', G: 'badge bg-gradient-success',
        P: 'badge bg-gradient-info', T: 'badge bg-gradient-primary',
      };
      return classes[method] || 'badge bg-gradient-secondary';
    },
    getPaymentMethodLabel(method) {
      const labels = { A: 'Apple', G: 'Google', P: 'Paddle', T: 'Toss', L: 'LemonSqueezy' };
      return labels[method] || method;
    },

    exportSettlementToExcel() {
      if (!this.settlementDetail || !this.settlementDetail.settlements) return;
      const workbook = XLSX.utils.book_new();

      const summaryData = [
        ['정산 상세내역'],
        [],
        ['지급서 ID', this.settlementDetail.buy_invoice_idx],
        ['총 인세 금액', this.settlementDetail.total_royalty_amount],
        ['총 결제 건수', this.settlementDetail.total_payment_count],
        ['정산 항목 수', this.settlementDetail.settlement_count],
        [],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, '요약');

      const detailHeaders = [
        '정산번호', '교재명', '에디터', '바코드', '정산기간', '결제건수',
        '인세금액', '인세화폐', '적용환율', '상태',
        '학습자', '결제금액', '쿠폰할인', '결제화폐',
        '결제방식', 'PG수수료율', '영업비율', '인세방식', '인세율', '1copy당인세', '개별인세', '결제일시',
      ];
      const detailData = [detailHeaders];

      this.settlementDetail.settlements.forEach((settlement, sIndex) => {
        const settlementPeriod = `${settlement.settlement_year}년 ${settlement.settlement_month}월`;
        const statusLabel = this.getSettlementStatusLabel(settlement.status);
        if (settlement.payments && settlement.payments.length > 0) {
          settlement.payments.forEach((payment) => {
            detailData.push([
              sIndex + 1, settlement.textbook_title, settlement.editor_name, settlement.textbook_barcode,
              settlementPeriod, settlement.payment_count,
              settlement.royalty_amount, settlement.royalty_currency, settlement.exchange_rate, statusLabel,
              payment.learner_name, payment.amount, payment.coupon_amount || 0, payment.currency_code,
              this.getPaymentMethodLabel(payment.payment_method),
              this.formatRate(payment.pg_fee_rate),
              this.hasSalesFee(payment) ? this.formatRate(payment.sales_fee_rate) : '-',
              this.isPerCopyRoyalty(payment) ? '정액(1copy당)' : '인세율',
              this.isPerCopyRoyalty(payment) ? '-' : `${payment.royalty_rate}%`,
              this.isPerCopyRoyalty(payment) ? this.perCopyLabel(payment) : '-',
              payment.royalty_amount,
              this.formatDateTime(payment.created_at),
            ]);
          });
        } else {
          detailData.push([
            sIndex + 1, settlement.textbook_title, settlement.editor_name, settlement.textbook_barcode,
            settlementPeriod, settlement.payment_count,
            settlement.royalty_amount, settlement.royalty_currency, settlement.exchange_rate, statusLabel,
            '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-',
          ]);
        }
      });

      const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
      detailSheet['!cols'] = [
        { wch: 8 }, { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 10 },
        { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
        { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 12 }, { wch: 18 },
      ];
      XLSX.utils.book_append_sheet(workbook, detailSheet, '정산상세');

      const fileName = `정산내역_${this.settlementDetail.buy_invoice_idx}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      this.$notifyVue({ type: "success", title: this.$t('label.138'), text: this.$t('txt.24') });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.sc-page {
  padding: 20px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: $p-font-body;
}

.sc-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

// Filter row
.sc-filter {
  display: flex;
  gap: 10px;
  align-items: stretch;
  flex-wrap: wrap;
}

.sc-filter__field {
  flex: 1;
  min-width: 180px;
}

.sc-filter__field--narrow {
  flex: 0 0 180px;
}

.sc-filter__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px !important;
  color: $p-ink-soft;
  pointer-events: none;
  z-index: 1;
}

:deep(.p-input-group) {
  position: relative;
  display: flex;
  align-items: center;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  transition: border-color 0.15s, box-shadow 0.15s;
}
:deep(.p-input-group:focus-within) {
  border-color: $p-point-deep;
  box-shadow: 0 0 0 3px $p-point-glow;
}
:deep(.p-input-group > .p-input) {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  height: 38px;
  padding: 0 12px 0 38px;
  width: 100%;
}
:deep(select.p-input) {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231F1B14' stroke-width='2.2' stroke-linecap='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 12px;
  padding-right: 30px !important;
}

// ACard head slot
.sc-card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: $p-ink;
}

.sc-card-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: $p-ink-mid;
}

// Cells
.sc-num {
  font-family: $p-font-mono;
  font-size: 11px;
  font-weight: 800;
  color: $p-ink-mid;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  padding: 2px 8px;
  border-radius: 5px;
}

.sc-invoice-no {
  font-family: $p-font-mono;
  font-weight: 700;
  color: $p-point-deep;
  font-size: 13px;
}

.sc-editor {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.sc-editor__avatar {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: $p-point-soft;
  color: $p-point-deep;
  border: 1px solid $p-point-deep;
  display: grid;
  place-items: center;
  font-family: $p-font-mono;
  font-weight: 800;
  font-size: 13px;
  flex-shrink: 0;

  &--sm {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    font-size: 11px;
  }
}

.sc-editor__text {
  min-width: 0;
}

.sc-editor__name {
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
}

.sc-editor__email {
  font-size: 11px;
  color: $p-ink-soft;
}

.sc-amount {
  font-family: $p-font-mono;
  font-weight: 700;
  color: $p-ok;
  font-size: 13px;

  &--big { font-size: 16px; font-weight: 800; }
  &--xlarge { font-size: 22px; font-weight: 800; }
}

.sc-date {
  font-family: $p-font-mono;
  font-size: 11px;
  color: $p-ink-mid;
}

.sc-mono {
  font-family: $p-font-mono;
  font-size: 12px;
}

.sc-muted { color: $p-ink-mid; }

.sc-coupon {
  color: $p-err;
  font-family: $p-font-mono;
  font-weight: 700;
}

// 영업비율 (0 이 아닌 행 강조)
.sc-sales-fee {
  color: $p-warn;
  font-weight: 800;
}

// 영업비 안내 문구
.sc-fee-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0 0 16px;
  padding: 10px 12px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  font-size: 12px;
  line-height: 1.5;
  color: $p-ink-mid;

  i {
    font-size: 16px !important;
    color: $p-point-deep;
    flex-shrink: 0;
  }
}

.sc-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.sc-foot {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 16px;
  border-top: 1px solid $p-line-soft;
}

// Detail Modal — 2x2 grid
.sc-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.sc-detail-section {
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: $p-radius;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sc-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
}

.sc-detail-label {
  font-size: 11px;
  font-weight: 700;
  color: $p-ink-mid;
  letter-spacing: 0.02em;
}

.sc-detail-value {
  font-size: 13px;
  color: $p-ink;
  font-weight: 600;
}

// Settlement Detail Modal
.sc-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;
}

.p-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid $p-line;
  border-top-color: $p-point;
  border-radius: 50%;
  animation: sc-spin 0.7s linear infinite;
}
@keyframes sc-spin {
  to { transform: rotate(360deg); }
}

.sc-loading-text {
  color: $p-ink-mid;
  font-size: 13px;
}

// Summary cards (3종)
.sc-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.sc-summary__card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: $p-radius;
  border: 1px solid;

  &--ok {
    background: $p-ok-soft;
    border-color: rgba(30, 140, 102, 0.25);
    .sc-summary__icon { color: $p-ok; }
    .sc-summary__value { color: $p-ok; }
  }

  &--warn {
    background: $p-warn-soft;
    border-color: rgba(201, 138, 10, 0.25);
    .sc-summary__icon { color: $p-warn; }
    .sc-summary__value { color: $p-warn; }
  }

  &--info {
    background: $p-info-soft;
    border-color: rgba(59, 123, 196, 0.25);
    .sc-summary__icon { color: $p-info; }
    .sc-summary__value { color: $p-info; }
  }
}

.sc-summary__icon {
  font-size: 28px !important;
  flex-shrink: 0;
}

.sc-summary__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.sc-summary__label {
  font-size: 11px;
  font-weight: 700;
  color: $p-ink-mid;
  letter-spacing: 0.02em;
}

.sc-summary__value {
  font-family: $p-font-mono;
  font-size: 16px;
  font-weight: 800;
}

// Settlement items
.sc-settlement {
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius-lg;
  overflow: hidden;
  margin-bottom: 16px;
}

.sc-settlement__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: $p-panel-alt;
  border-bottom: 1px solid $p-line;
  flex-wrap: wrap;
}

.sc-settlement__title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.sc-settlement__num {
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: $p-ink;
  color: $p-point;
  font-family: $p-font-mono;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}

.sc-settlement__title {
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: $p-ink;
}

.sc-settlement__meta {
  margin: 2px 0 0;
  font-size: 11px;
  color: $p-ink-mid;
}

.sc-settlement__right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.sc-settlement__info {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid $p-line-soft;
}

.sc-mini-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sc-mini-info__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $p-ink-soft;
}

.sc-mini-info__value {
  font-family: $p-font-mono;
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
}

.sc-settlement__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 16px;
  background: $p-panel-alt;
  border-top: 1px solid $p-line-soft;
}

.sc-settlement__foot-label {
  font-size: 13px;
  font-weight: 700;
  color: $p-ink-mid;
}

.sc-grand-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 18px;
  background: $p-point-soft;
  border: 1px solid $p-point-deep;
  border-radius: $p-radius-lg;
  margin-top: 8px;
}

.sc-grand-total__label {
  font-size: 14px;
  font-weight: 800;
  color: $p-point-deep;
  letter-spacing: 0.02em;
}

@media (max-width: 1100px) {
  .sc-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .sc-summary { grid-template-columns: 1fr; }
  .sc-detail-grid { grid-template-columns: 1fr; }
  .sc-settlement__info { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 640px) {
  .sc-stats { grid-template-columns: 1fr; }
  .sc-filter { flex-direction: column; }
  .sc-filter__field--narrow { flex: 1 1 100%; }
  .sc-settlement__info { grid-template-columns: 1fr; }
}
</style>
